import React, { memo, useContext, useMemo } from "react";

import { List } from "Components/NewList";
import { getMissionStatusField } from "Components/MissionPlanner/components/MissionPlannerLists/utils";
import { getLoopsField, getStopsField } from "Components/common/TableElements";
import { MissionsPageSearchContext } from "Components/MissionsPage/SearchContextHelper";
import {
  getAssignedToField,
  getMissionNameField,
  parseTime,
} from "Components/LiveMissionsTable/utils";
import { getMissionDeploymentDate } from "./liveMissionsUtils";
import { OverrideMissionWarning } from "./OverrideMissionWarning";
import { LiveMissionsWrapperStyled } from "./styles";
import { useLiveMissionsData } from "./useLiveMissionsData";
import { useLiveMissionsActions } from "./useLiveMissionsActions";

type LiveMissionsDisplayData = {
  id: string;
  name: JSX.Element;
  rawName: string;
  assignedTo: JSX.Element;
  status: JSX.Element;
  stops: JSX.Element;
  loops: JSX.Element;
  vehicleId: string;
  deployedAt: string;
  startedExecutionAt: string;
};

export const LiveMissionsTable = memo(() => {
  const { searchValue } = useContext(MissionsPageSearchContext);

  // Use custom hooks for data and actions
  const { vehicles, vehicleTypes, liveMissions, vehicleStates, isLoading } =
    useLiveMissionsData(searchValue);

  const {
    missionActions,
    overrideMissionWarningOptions,
    handleMissionNameClick,
    handleCloseOverrideWarning,
    handleSuccessOverrideWarning,
  } = useLiveMissionsActions(vehicles);

  const optionsDropdownOffset = useMemo(() => ({ top: 40, right: 0 }), []);

  const headerData = useMemo(
    () => ({
      name: "Mission Name",
      assignedTo: "Vehicle Assigned",
      status: "Status",
      loops: "Cycles",
      deployedAt: "Deployed at",
      startedExecutionAt: "Started Execution at",
    }),
    []
  );

  // Transform missions data for display
  const preparedLiveMissionsData: LiveMissionsDisplayData[] = useMemo(() => {
    if (!liveMissions || isLoading) return [];

    return liveMissions.map((liveMission: any) => ({
      id: liveMission.id,
      name: getMissionNameField(
        liveMission.name,
        liveMission.id,
        handleMissionNameClick
      ),
      rawName: liveMission.name,
      assignedTo: getAssignedToField(
        liveMission.vehicle,
        vehicles,
        vehicleTypes,
        vehicleStates
      ),
      status: getMissionStatusField(liveMission.status),
      stops: getStopsField(liveMission.passedStops, liveMission.totalStops),
      loops: getLoopsField(liveMission.passedLoops, liveMission.totalLoops),
      vehicleId: liveMission.vehicle,
      deployedAt: parseTime(getMissionDeploymentDate(liveMission)),
      startedExecutionAt: parseTime(
        liveMission.startedExecutionAt || liveMission.timeStamp
      ),
    }));
  }, [
    liveMissions,
    vehicles,
    vehicleTypes,
    handleMissionNameClick,
    vehicleStates,
    isLoading,
  ]);

  return (
    <LiveMissionsWrapperStyled data-test-id="show-live-missions-table">
      <List
        data={preparedLiveMissionsData}
        headerData={headerData}
        options={missionActions}
        optionsDropdownOffset={optionsDropdownOffset}
      />
      {overrideMissionWarningOptions.isShown && (
        <OverrideMissionWarning
          vehicleName={overrideMissionWarningOptions.vehicleName}
          handleCloseAction={handleCloseOverrideWarning}
          handleSuccessAction={handleSuccessOverrideWarning}
        />
      )}
    </LiveMissionsWrapperStyled>
  );
});
